import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { BarraProgresso, Botao, ErrorState, Modal, Nota, Skeleton } from '../../../ui';
import PainelExecucaoSync from '../../ti/PainelExecucaoSync';
import { formatarDataHora } from '../../../utils/data';
import { extrairMensagemErro } from '../../../utils/apiError';
import {
  StatusDescoberta,
  dispararBackfillDescoberta,
  dispararDescoberta,
  getStatusDescoberta,
  pararBackfillDescoberta,
} from '../../../api/sga-descoberta/sga-descobertaApi';

/**
 * Descoberta de associados criados no SGA (at-32, Plano F).
 * Reescrito sobre o design system na at-67 (frente D/D5).
 *
 * Mesmo desenho dos modais de sincronização: estado atual, progresso alimentado
 * por polling e disparo manual. A diferença é ter **duas** barras — a varredura
 * do que é novo (que o cron já faz de 2 em 2 horas) e a carga inicial do
 * histórico, que é retomável e por isso mostra o próprio avanço
 * (`backfillAte / ponta`).
 *
 * 🔴 **Duas correções de comportamento na at-67:**
 *
 *   1. **"Interromper carga inicial" não tratava falha.** Era
 *      `onClick={async () => { const r = await pararBackfillDescoberta(); … }}`
 *      sem `try/catch` — e `pararBackfillDescoberta` é a **única** função daquela
 *      API que não embrulha o erro. Falha ali virava *unhandled rejection*: nada
 *      na tela, nada no toast, o botão como se nada tivesse acontecido.
 *   2. **A barra do histórico era AZUL `#2563eb`** — uma cor que não existe nos
 *      tokens do design system, inventada para este modal. Agora é a barra do kit,
 *      na cor da marca.
 *
 * Mais a falha da leitura, que deixou de ser um toast a cada 3 s (ver o cabeçalho
 * de `ModalSincronizarProdutos.tsx`).
 */

const POLLING_MS = 3000;

const FASE_LABEL: Record<string, string> = {
  LOCALIZACAO_PONTA: 'localizando o ponto de partida no SGA',
  DESCOBERTA: 'procurando associados novos',
  BACKFILL: 'varrendo o histórico',
};

interface ModalDescobertaAssociadosProps {
  isOpen: boolean;
  onClose: () => void;
}

const ModalDescobertaAssociados: React.FC<ModalDescobertaAssociadosProps> = ({
  isOpen,
  onClose,
}) => {
  const [status, setStatus] = useState<StatusDescoberta | null>(null);
  const [falhaLeitura, setFalhaLeitura] = useState('');
  /** Qual ação está em curso — para travar só o botão certo. */
  const [acao, setAcao] = useState<'descoberta' | 'backfill' | 'parar' | null>(null);

  const execucao = status?.execucao ?? null;
  const marcador = status?.marcador ?? null;
  const emAndamento = execucao?.status === 'EM_ANDAMENTO';

  const carregar = useCallback(async () => {
    try {
      setStatus(await getStatusDescoberta());
      setFalhaLeitura('');
    } catch (falha) {
      setFalhaLeitura(extrairMensagemErro(falha, 'Não foi possível consultar o estado agora.'));
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    setFalhaLeitura('');
    void carregar();

    const intervalo = setInterval(() => {
      void carregar();
    }, POLLING_MS);

    return () => clearInterval(intervalo);
  }, [isOpen, carregar]);

  /**
   * Dispara uma das três ações.
   *
   * ⚠️ O `try/catch` cobre as **três** — inclusive a de interromper, que é a que
   * não o tinha (ver o aviso no topo).
   */
  const disparar = async (
    qual: 'descoberta' | 'backfill' | 'parar',
    executar: () => Promise<{ mensagem: string }>,
    sucessoPadrao: string,
    falhaPadrao: string,
  ) => {
    setAcao(qual);
    try {
      const resposta = await executar();
      toast.success(resposta.mensagem || sucessoPadrao);
      await carregar();
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, falhaPadrao));
    } finally {
      setAcao(null);
    }
  };

  const ocupado = acao !== null;
  const semLeituraNenhuma = !status && !!falhaLeitura;

  const avisos: React.ReactNode[] = [];
  if (execucao && execucao.foraDeCarteira > 0) {
    avisos.push(
      <>
        {execucao.foraDeCarteira} associado(s) do SGA ficaram de fora: o voluntário deles não tem
        usuário no SAAC. Cadastre esses consultores em <b>Editar Usuário</b> e rode de novo — nada
        foi criado para eles.
      </>,
    );
  }
  if (execucao && execucao.falhas > 0) {
    avisos.push(
      <>
        {execucao.falhas} cadastro(s) não puderam ser importados (dados obrigatórios faltando no
        SGA):
        <ul className="tw-mb-0 tw-mt-1 tw-list-disc tw-pl-5">
          {execucao.exemplosFalhas.map((falha) => (
            <li key={falha}>{falha}</li>
          ))}
        </ul>
      </>,
    );
  }

  return (
    <Modal
      aberto={isOpen}
      titulo="Descoberta de Associados (SGA)"
      descricao="Traz para o SAAC os associados que foram criados direto no SGA."
      ocupado={ocupado}
      aoFechar={onClose}
      rodape={
        <Botao variante="secundario" onClick={onClose} disabled={ocupado}>
          Fechar
        </Botao>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        {!status && !falhaLeitura && <Skeleton linhas={3} altura="tw-h-12" />}

        {semLeituraNenhuma && (
          <ErrorState
            mensagem="Não foi possível consultar o estado da descoberta."
            detalhe={falhaLeitura}
            aoTentarNovamente={() => void carregar()}
          />
        )}

        {status && marcador && (
          <>
            {falhaLeitura && (
              <Nota variante="atencao">
                Não foi possível atualizar o estado agora — o que está abaixo é da última leitura
                que deu certo. A tela tenta de novo sozinha a cada poucos segundos.
              </Nota>
            )}

            <div className="tw-flex tw-flex-col tw-gap-1 tw-text-sm tw-text-texto-corpo">
              <p className="tw-m-0">
                <b>Associados importados do SGA:</b> {status.associadosImportadosDoSga}
              </p>

              {marcador.definido ? (
                <>
                  <p className="tw-m-0">
                    <b>Varrido até o código:</b> {marcador.ponta}{' '}
                    <span className="tw-text-texto-suave">
                      (definido em {formatarDataHora(marcador.pontaEm)})
                    </span>
                  </p>
                  <p className="tw-m-0">
                    <b>Histórico:</b>{' '}
                    {marcador.historicoCoberto
                      ? 'coberto por inteiro'
                      : `${marcador.backfillAte} de ${marcador.ponta} (${marcador.percentualBackfill}%)`}
                  </p>
                </>
              ) : (
                <p className="tw-m-0 tw-text-texto-suave">
                  A varredura ainda não rodou. A primeira execução apenas localiza o ponto de
                  partida no SGA — nada é importado nela.
                </p>
              )}
            </div>

            {/* Progresso do histórico: é a barra que interessa entre uma execução e
                outra, porque o backfill é retomável e leva várias passadas. */}
            {marcador.definido && !marcador.historicoCoberto && (
              <div className="tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-white tw-p-3">
                <p className="tw-mb-2 tw-mt-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-texto-suave">
                  Carga inicial do histórico
                </p>
                <BarraProgresso percent={marcador.percentualBackfill} semMoldura />
              </div>
            )}

            {execucao && (
              <PainelExecucaoSync
                status={execucao.status}
                faseRotulo={FASE_LABEL[execucao.fase] ?? execucao.fase}
                origem={execucao.origem}
                iniciadoEm={execucao.iniciadoEm}
                percentual={execucao.percentual}
                progresso={{ feitas: execucao.consultadas, total: execucao.total }}
                erro={execucao.erro}
                avisos={avisos}
                resumo={
                  <>
                    {execucao.associadosImportados} associado(s) · {execucao.veiculosImportados}{' '}
                    veículo(s) · {execucao.codigosVarridos} código(s) varrido(s) ·{' '}
                    {execucao.jaConhecidos} já conhecido(s)
                  </>
                }
              />
            )}
          </>
        )}

        <div className="tw-flex tw-flex-col tw-gap-2">
          <Botao
            variante="primario"
            larguraTotal
            carregando={acao === 'descoberta'}
            disabled={ocupado || emAndamento}
            onClick={() =>
              disparar(
                'descoberta',
                dispararDescoberta,
                'Varredura iniciada.',
                'Não foi possível iniciar a varredura.',
              )
            }
          >
            {emAndamento ? 'Varredura em andamento...' : 'Executar agora'}
          </Botao>
          <Nota>
            Procura associados criados no SGA depois da última passada e importa os que pertencem a
            uma carteira do SAAC. Também roda sozinha a cada 2 horas.
          </Nota>
        </div>

        {marcador && !marcador.historicoCoberto && (
          <div className="tw-flex tw-flex-col tw-gap-2">
            <Botao
              variante="secundario"
              larguraTotal
              carregando={acao === 'backfill'}
              disabled={ocupado || emAndamento}
              onClick={() =>
                disparar(
                  'backfill',
                  dispararBackfillDescoberta,
                  'Carga inicial iniciada.',
                  'Não foi possível iniciar a carga inicial.',
                )
              }
            >
              {emAndamento ? 'Aguardando a varredura atual...' : 'Carga inicial (histórico)'}
            </Botao>
            <Nota>
              Traz os associados que já existiam no SGA. Um único disparo trabalha em lotes até
              cobrir o histórico inteiro — dá para interromper e retomar depois do ponto em que
              parou.
            </Nota>

            {emAndamento && execucao?.fase === 'BACKFILL' && (
              <Botao
                variante="secundario"
                larguraTotal
                carregando={acao === 'parar'}
                disabled={ocupado}
                onClick={() =>
                  disparar(
                    'parar',
                    pararBackfillDescoberta,
                    'Interrupção solicitada.',
                    'Não foi possível interromper a carga inicial.',
                  )
                }
              >
                Interromper carga inicial
              </Botao>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ModalDescobertaAssociados;
